import { useState } from "react"
import ReactTags from "react-tag-autocomplete"
import useSWR, { mutate } from "swr"
import { HashTag } from "src/models/tieup/HashTag"
import { postHashTag } from "../../models/tieup/postHashTag"
import { useTieupToast } from "../hooks/useTieupToast"
import { fetchHashTags } from "../../models/tieup/fetchHashTags"
import styles from "./HashTags.module.scss"

export type HashTagsProps = {
  /** 自分のハッシュタグ */
  hashtags: HashTag[]
  /** ハッシュタグの追加 */
  onSubmit: (newTags: Partial<HashTag>[]) => void
  /** タグ最大数 */
  maxLength?: number
}

const HashTags: React.FC<HashTagsProps> = ({
  hashtags,
  onSubmit,
  maxLength = 10,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const { addToastError } = useTieupToast()

  const { data: suggestions } = useSWR<HashTag[]>(
    "/v1/hashtag",
    () => {
      return fetchHashTags()
    },
    {
      revalidateOnFocus: false,
    },
  )

  /** タグ追加 */
  const onAddition = async (tag: HashTag) => {
    // タグ名は10文字まで
    if (tag.name.length > 10) {
      alert("タグ名は10文字までです")

      return
    }

    // 登録できるタグは10個まで
    if (hashtags.length >= maxLength) {
      alert("登録できるタグは10個までです")

      return
    }

    // すでに登録されているタグの場合は何もしない
    const hashtagNames = hashtags.map((h) => h.name)
    if (hashtagNames.includes(tag.name)) return

    // すでに存在しているタグの場合は、そのままタグを登録
    if (tag.id !== undefined) {
      onSubmit([...hashtags, tag])

      return
    }

    // 存在していないタグの場合は、全体のタグリストへの登録をしてタグを追加
    const existsInSuggestions = suggestions?.some((v) => v.name === tag.name)
    if (!existsInSuggestions) {
      try {
        const { id, name } = await postHashTag(tag.name)
        await mutate("/v1/hashtag")
        const postedTag = { id, name }
        onSubmit([...hashtags, postedTag])
      } catch (error) {
        setIsLoading(false)
        if (error.response.status === 403) {
          alert("このタグの使用は禁止されています")
        } else {
          addToastError("タグの追加に失敗しました。")
        }

        // eslint-disable-next-line no-console
        console.log(error)
      }
    }

    setIsLoading(false)
  }

  /** タグ削除 */
  const onDelete = (i: number) => {
    const newTags = [...hashtags]
    newTags.splice(i, 1)
    onSubmit(newTags)
  }

  if (!hashtags || !suggestions) return null

  return (
    <div {...rest} className={styles.HashTags}>
      <ReactTags
        tags={hashtags}
        suggestions={suggestions}
        onAddition={onAddition}
        onDelete={onDelete}
        allowNew={true}
        placeholderText=""
        inputAttributes={{ disabled: isLoading }}
        minQueryLength={1} // タグに使える最小文字数
      />
      {isLoading && <div>ローディング中</div>}
    </div>
  )
}

export default HashTags
